import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, X, Check, RotateCcw, Sparkles, Brain, ChevronRight, Home, Hash } from 'lucide-react';

// Confetti function
const triggerConfetti = () => {
  try {
    import('canvas-confetti').then((confettiModule) => {
      const confetti = confettiModule.default;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10B981', '#14B8A6', '#059669']
      });
    });
  } catch (e) {
    console.log('Confetti not available');
  }
};

// Quiz Questions sur la numérologie
const QUIZ_QUESTIONS = [
  {
    id: 1,
    category: "Chemin de vie",
    question: "Comment calcule-t-on le chemin de vie ?",
    options: [
      "En additionnant les lettres du prénom",
      "En additionnant tous les chiffres de la date de naissance",
      "En comptant les voyelles du nom complet",
      "En multipliant le jour par le mois"
    ],
    correctAnswer: 1,
    explanation: "Le chemin de vie se calcule en additionnant tous les chiffres de la date de naissance (jour + mois + année) et en les réduisant à un seul chiffre, sauf pour les nombres maîtres (11, 22, 33)."
  },
  {
    id: 2,
    category: "Nombres maîtres",
    question: "Quels sont les trois nombres maîtres en numérologie ?",
    options: [
      "1, 2, 3",
      "7, 8, 9",
      "11, 22, 33",
      "10, 20, 30"
    ],
    correctAnswer: 2,
    explanation: "Les nombres maîtres sont 11, 22 et 33. Ils ne sont pas réduits lors des calculs car ils possèdent une vibration particulièrement puissante et des significations spéciales."
  },
  {
    id: 3,
    category: "Nombre d'expression",
    question: "Que révèle le nombre d'expression ?",
    options: [
      "La mission de vie",
      "Les talents naturels et la personnalité globale",
      "Les désirs profonds",
      "L'année personnelle"
    ],
    correctAnswer: 1,
    explanation: "Le nombre d'expression, calculé à partir de toutes les lettres du nom complet, révèle les talents naturels, les compétences innées et la personnalité visible aux autres."
  },
  {
    id: 4,
    category: "Nombre intime",
    question: "Comment calcule-t-on le nombre intime (ou de l'âme) ?",
    options: [
      "Avec les consonnes du nom",
      "Avec les voyelles du nom uniquement",
      "Avec la date de naissance",
      "Avec les initiales uniquement"
    ],
    correctAnswer: 1,
    explanation: "Le nombre intime se calcule uniquement avec les voyelles du nom complet. Il révèle les désirs profonds, les motivations secrètes et les aspirations de l'âme."
  },
  {
    id: 5,
    category: "Nombre de réalisation",
    question: "Le nombre de réalisation est calculé avec :",
    options: [
      "Les voyelles uniquement",
      "Les consonnes uniquement",
      "Toutes les lettres",
      "La date de naissance"
    ],
    correctAnswer: 1,
    explanation: "Le nombre de réalisation utilise uniquement les consonnes du nom complet. Il représente l'image que l'on projette aux autres, le masque social et les réalisations concrètes."
  },
  {
    id: 6,
    category: "Cycles",
    question: "Combien d'années dure un cycle complet en numérologie ?",
    options: [
      "7 ans",
      "9 ans",
      "12 ans",
      "10 ans"
    ],
    correctAnswer: 1,
    explanation: "En numérologie, un cycle complet dure 9 ans. Chaque année personnelle (de 1 à 9) a une signification particulière et marque une étape dans ce cycle d'évolution."
  },
  {
    id: 7,
    category: "Année personnelle",
    question: "Quelle est la signification de l'année personnelle 1 ?",
    options: [
      "Bilan et introspection",
      "Nouveaux départs et initiatives",
      "Relations et partenariats",
      "Récolte et abondance"
    ],
    correctAnswer: 1,
    explanation: "L'année personnelle 1 marque le début d'un nouveau cycle de 9 ans. C'est le moment idéal pour les nouveaux départs, prendre des initiatives et planter les graines de nouveaux projets."
  },
  {
    id: 8,
    category: "Année personnelle",
    question: "L'année personnelle 9 est associée à :",
    options: [
      "De nouveaux commencements",
      "La croissance et l'expansion",
      "L'achèvement, le bilan et le lâcher-prise",
      "La stabilité et les fondations"
    ],
    correctAnswer: 2,
    explanation: "L'année 9 clôt le cycle de 9 ans. C'est une période de bilan, d'achèvement et de lâcher-prise, préparant le terrain pour le nouveau cycle qui commence avec l'année 1."
  },
  {
    id: 9,
    category: "Dettes karmiques",
    question: "Quels sont les nombres de dettes karmiques ?",
    options: [
      "11, 22, 33",
      "13, 14, 16, 19",
      "1, 4, 7",
      "2, 5, 8"
    ],
    correctAnswer: 1,
    explanation: "Les dettes karmiques sont les nombres 13, 14, 16 et 19. Ils indiquent des leçons de vie spécifiques à apprendre, souvent liées à des abus ou des erreurs des vies passées."
  },
  {
    id: 10,
    category: "Dettes karmiques",
    question: "La dette karmique 16 est associée à :",
    options: [
      "Le travail et la paresse",
      "L'ego, l'orgueil et les relations",
      "Les responsabilités familiales",
      "L'argent et les possessions"
    ],
    correctAnswer: 1,
    explanation: "La dette 16 concerne l'ego démesuré et l'orgueil. Elle invite à développer l'humilité, à reconnaître ses erreurs et à construire des relations basées sur l'authenticité."
  },
  {
    id: 11,
    category: "Signification des nombres",
    question: "Le nombre 7 en numérologie représente :",
    options: [
      "L'action et le leadership",
      "L'harmonie et les relations",
      "La spiritualité et l'introspection",
      "Le changement et la liberté"
    ],
    correctAnswer: 2,
    explanation: "Le 7 est le nombre de la spiritualité, de l'analyse, de l'introspection et de la recherche de la vérité. Les personnes 7 sont souvent attirées par la philosophie et la méditation."
  },
  {
    id: 12,
    category: "Signification des nombres",
    question: "Quel nombre symbolise la communication et la créativité ?",
    options: [
      "Le 1",
      "Le 3",
      "Le 4",
      "Le 8"
    ],
    correctAnswer: 1,
    explanation: "Le 3 est le nombre de l'expression, de la créativité et de la communication. Il représente l'optimisme, la joie de vivre et les talents artistiques."
  },
  {
    id: 13,
    category: "Signification des nombres",
    question: "Le nombre 8 en numérologie est associé à :",
    options: [
      "L'amour et les émotions",
      "Le pouvoir, l'argent et la réussite matérielle",
      "La solitude et l'introspection",
      "Les voyages et l'aventure"
    ],
    correctAnswer: 1,
    explanation: "Le 8 symbolise le pouvoir, la réussite matérielle, l'ambition et l'argent. Il représente aussi l'équilibre entre le matériel et le spirituel (forme du symbole infini)."
  },
  {
    id: 14,
    category: "Heures miroir",
    question: "Que signifie voir régulièrement 11:11 ?",
    options: [
      "Malchance imminente",
      "Alignement spirituel et éveil de conscience",
      "Problèmes financiers à venir",
      "Besoin de dormir plus"
    ],
    correctAnswer: 1,
    explanation: "11:11 est considéré comme un portail d'éveil spirituel. Voir cette heure régulièrement peut indiquer que vous êtes aligné avec votre chemin de vie et que l'univers vous envoie un message."
  },
  {
    id: 15,
    category: "Heures miroir",
    question: "L'heure miroir 22:22 est associée à :",
    options: [
      "La créativité personnelle",
      "La réalisation de grands projets et la maîtrise",
      "Les relations amoureuses",
      "Les problèmes de santé"
    ],
    correctAnswer: 1,
    explanation: "22:22 est lié au nombre maître 22, le 'maître bâtisseur'. Cette heure symbolise la capacité à concrétiser de grands projets et à atteindre la maîtrise dans son domaine."
  },
  {
    id: 16,
    category: "Nombres manquants",
    question: "Que signifient les nombres manquants dans un thème ?",
    options: [
      "Des faiblesses permanentes",
      "Des qualités à développer dans cette vie",
      "Des dons innés",
      "Des années de malchance"
    ],
    correctAnswer: 1,
    explanation: "Les nombres absents du nom complet représentent des qualités et énergies à développer. Ce sont des opportunités d'apprentissage et de croissance, pas des handicaps."
  },
  {
    id: 17,
    category: "Pinnacles",
    question: "Qu'est-ce qu'un pinnacle en numérologie ?",
    options: [
      "Un nombre malchanceux",
      "Une période de vie avec des leçons spécifiques",
      "Le nombre du destin",
      "Un calcul pour l'année personnelle"
    ],
    correctAnswer: 1,
    explanation: "Les pinnacles sont 4 grandes périodes de vie, chacune avec ses défis et opportunités spécifiques. Ils indiquent les thèmes majeurs et les leçons de chaque phase de l'existence."
  },
  {
    id: 18,
    category: "Histoire",
    question: "Quel philosophe grec est considéré comme le père de la numérologie occidentale ?",
    options: [
      "Platon",
      "Aristote",
      "Pythagore",
      "Socrate"
    ],
    correctAnswer: 2,
    explanation: "Pythagore (570-495 av. J.-C.) est considéré comme le père de la numérologie occidentale. Sa célèbre phrase 'Tout est nombre' résume sa vision que les nombres régissent l'univers."
  },
  {
    id: 19,
    category: "Conversion lettres-nombres",
    question: "Dans le système pythagoricien, quelle valeur a la lettre A ?",
    options: [
      "0",
      "1",
      "9",
      "26"
    ],
    correctAnswer: 1,
    explanation: "Dans le système pythagoricien utilisé en numérologie moderne, A=1, B=2, C=3... jusqu'à I=9, puis J=1, K=2, etc. La lettre A vaut donc 1."
  },
  {
    id: 20,
    category: "Principes",
    question: "Quel est le principe fondamental de la numérologie ?",
    options: [
      "Prédire exactement l'avenir",
      "Les nombres révèlent des tendances et potentiels, pas des certitudes",
      "Seule la date de naissance compte",
      "Les nombres négatifs apportent la malchance"
    ],
    correctAnswer: 1,
    explanation: "La numérologie révèle des tendances et potentiels, jamais des certitudes absolues. Elle est un outil de connaissance de soi et d'orientation, pas de prédiction fataliste."
  }
];

const NumerologiaQuizPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const question = QUIZ_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === question.correctAnswer;
    
    setAnswers([...answers, {
      questionId: question.id,
      selected: selectedAnswer,
      correct: question.correctAnswer,
      isCorrect
    }]);

    if (isCorrect) {
      setScore(score + 1);
    }

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      if (score >= QUIZ_QUESTIONS.length * 0.7) {
        triggerConfetti();
      }
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setAnswers([]);
    setQuizCompleted(false);
    setShowExplanation(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / QUIZ_QUESTIONS.length) * 100;
    if (percentage >= 90) return { title: "Excellent !", message: "Vous êtes un expert en numérologie !", emoji: "🏆" };
    if (percentage >= 70) return { title: "Très bien !", message: "Vous maîtrisez les bases de la numérologie !", emoji: "🌟" };
    if (percentage >= 50) return { title: "Bien !", message: "Continuez à explorer la numérologie.", emoji: "👍" };
    return { title: "Courage !", message: "Découvrez la base de connaissances pour progresser.", emoji: "📚" };
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Chemin de vie": "bg-emerald-500",
      "Nombres maîtres": "bg-amber-500",
      "Nombre d'expression": "bg-teal-500",
      "Nombre intime": "bg-cyan-500",
      "Nombre de réalisation": "bg-emerald-600",
      "Cycles": "bg-green-500",
      "Année personnelle": "bg-teal-600",
      "Dettes karmiques": "bg-rose-500",
      "Signification des nombres": "bg-emerald-500",
      "Heures miroir": "bg-cyan-500",
      "Nombres manquants": "bg-amber-600",
      "Pinnacles": "bg-teal-500",
      "Histoire": "bg-emerald-700",
      "Conversion lettres-nombres": "bg-green-600",
      "Principes": "bg-emerald-600"
    };
    return colors[category] || "bg-gray-500";
  };

  if (quizCompleted) {
    const scoreInfo = getScoreMessage();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-12 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl border border-emerald-200 shadow-lg p-8 text-center"
          >
            <div className="text-6xl mb-6">{scoreInfo.emoji}</div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">{scoreInfo.title}</h1>
            <p className="text-emerald-700 text-lg mb-8">{scoreInfo.message}</p>
            
            <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border border-emerald-200">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Trophy className="h-8 w-8 text-amber-500" />
                <span className="text-4xl font-bold text-emerald-700">{score}/{QUIZ_QUESTIONS.length}</span>
              </div>
              <p className="text-sm text-emerald-600">
                {Math.round((score / QUIZ_QUESTIONS.length) * 100)}% de bonnes réponses
              </p>
            </div>

            {/* Detailed Results */}
            <div className="text-left mb-8">
              <h3 className="font-semibold mb-4 text-gray-800">Détail par catégorie :</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(
                  answers.reduce((acc, answer) => {
                    const q = QUIZ_QUESTIONS.find(q => q.id === answer.questionId);
                    if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
                    acc[q.category].total++;
                    if (answer.isCorrect) acc[q.category].correct++;
                    return acc;
                  }, {})
                ).map(([category, stats]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`}></span>
                      {category}
                    </span>
                    <span className={stats.correct === stats.total ? "text-emerald-600 font-medium" : "text-gray-500"}>
                      {stats.correct}/{stats.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={restartQuiz}
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-500"
                data-testid="restart-quiz-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Recommencer
              </Button>
              <Button
                onClick={() => navigate('/numerologia/knowledge')}
                variant="outline"
                className="flex-1 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <Brain className="h-4 w-4 mr-2" />
                Apprendre
              </Button>
              <Button
                onClick={() => navigate('/numerologia')}
                variant="ghost"
                className="flex-1 rounded-full text-gray-600 hover:bg-gray-100"
              >
                <Home className="h-4 w-4 mr-2" />
                Accueil
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-12 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/numerologia')}
            className="mb-6 rounded-full text-gray-600 hover:bg-emerald-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Hash className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quiz Numérologie</h1>
                <p className="text-sm text-emerald-600">Testez vos connaissances</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">{currentQuestion + 1}/{QUIZ_QUESTIONS.length}</p>
              <p className="text-xs text-gray-500">Score: {score}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-3xl border border-emerald-200 shadow-lg overflow-hidden"
          >
            {/* Category Badge */}
            <div className={`px-6 py-3 ${getCategoryColor(question.category)}`}>
              <span className="text-white text-sm font-medium">{question.category}</span>
            </div>

            <div className="p-6 md:p-8">
              {/* Question */}
              <h2 className="text-xl md:text-2xl mb-8 leading-relaxed text-gray-900 font-semibold">
                {question.question}
              </h2>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {question.options.map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrect = index === question.correctAnswer;
                  const showCorrectness = showExplanation;

                  let bgClass = "bg-gray-50 hover:bg-emerald-50 border-gray-200";
                  let iconClass = null;

                  if (showCorrectness) {
                    if (isCorrect) {
                      bgClass = "bg-emerald-50 border-emerald-500";
                      iconClass = <Check className="h-5 w-5 text-emerald-600" />;
                    } else if (isSelected && !isCorrect) {
                      bgClass = "bg-red-50 border-red-400";
                      iconClass = <X className="h-5 w-5 text-red-500" />;
                    }
                  } else if (isSelected) {
                    bgClass = "bg-emerald-100 border-emerald-400";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${bgClass} ${
                        showExplanation ? 'cursor-default' : 'cursor-pointer'
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isSelected && !showCorrectness ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-sm md:text-base text-gray-700">{option}</span>
                      </div>
                      {iconClass}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {showExplanation && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className={`p-4 rounded-xl ${
                      selectedAnswer === question.correctAnswer 
                        ? 'bg-emerald-50 border border-emerald-300' 
                        : 'bg-amber-50 border border-amber-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Sparkles className={`h-5 w-5 mt-0.5 ${
                          selectedAnswer === question.correctAnswer 
                            ? 'text-emerald-600' 
                            : 'text-amber-600'
                        }`} />
                        <div>
                          <p className={`font-medium mb-1 ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-emerald-700' 
                              : 'text-amber-700'
                          }`}>
                            {selectedAnswer === question.correctAnswer ? 'Correct !' : 'Explication'}
                          </p>
                          <p className={`text-sm ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-emerald-600' 
                              : 'text-amber-600'
                          }`}>
                            {question.explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex justify-end">
                {!showExplanation ? (
                  <Button
                    onClick={handleValidate}
                    disabled={selectedAnswer === null}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-8"
                    data-testid="validate-btn"
                  >
                    Valider
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-8"
                    data-testid="next-btn"
                  >
                    {currentQuestion < QUIZ_QUESTIONS.length - 1 ? (
                      <>
                        Question suivante
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Voir mes résultats
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NumerologiaQuizPage;
